// Teacher moderation: assign buckets to flagged answers and resolve reported
// question issues. Answer keys stay within the role-gated question review.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { Button, Card } from '@/components/ds';
import { MathText } from '@/components/exam/MathText';
import {
  marksForBin,
  type AnswerReviewDTO,
  type BinColor,
  type ResolveAnswerReviewResponse,
} from '@cwy/config/contracts';
import { BIN_LABEL, BIN_ORDER, binRamp } from '@/lib/bins';

interface ReportQuestion {
  id: string;
  type: string;
  prompt: string;
  options: string[] | null;
  correctOptionIdx: number | null;
  correctAnswer: string | null;
  subtopic: { name: string; topic: { name: string } };
}
interface Report {
  id: string;
  questionId: string;
  reason: string;
  note: string | null;
  board: string;
  grade: string;
  createdAt: string;
  question: ReportQuestion | null;
}

const REASON_LABEL: Record<string, string> = {
  wrong_answer: 'Wrong answer',
  typo: 'Typo / formatting',
  unclear: 'Unclear',
  off_syllabus: 'Off-syllabus',
  other: 'Other',
};

export function ReviewQueuePage() {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ['admin', 'reports', 'OPEN'],
    queryFn: () => apiGet<Report[]>('/admin/reports?status=OPEN'),
  });
  const answers = useQuery({
    queryKey: ['teacher', 'answer-reviews'],
    queryFn: () => apiGet<AnswerReviewDTO[]>('/teacher/answer-reviews'),
    refetchInterval: (query) =>
      query.state.data?.some((answer) => answer.teacherBin) ? 2_000 : 30_000,
  });

  const fixKey = useMutation({
    mutationFn: (v: { questionId: string; correctOptionIdx: number }) =>
      apiPatch(`/admin/questions/${v.questionId}`, { correctOptionIdx: v.correctOptionIdx }),
  });
  const resolve = useMutation({
    mutationFn: (v: { id: string; status: 'RESOLVED' | 'DISMISSED' }) =>
      apiPatch(`/admin/reports/${v.id}`, { status: v.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'reports', 'OPEN'] }),
  });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-bold text-forest">Review queue</h1>
        <p className="mt-1 text-ink-soft">
          Answers awaiting your review and questions reported by students.
        </p>
      </div>

      <section className="flex flex-col gap-3" aria-label="Answers awaiting review">
        <h2 className="text-lg font-semibold text-forest">Answers awaiting review</h2>
        {answers.isLoading && <p className="text-ink-soft">Loading answers…</p>}
        {answers.isError && <p className="text-danger">Could not load answers awaiting review.</p>}
        {answers.data?.length === 0 && <p className="text-ink-soft">No answers flagged for you.</p>}
        {answers.data?.map((answer) => (
          <AnswerReviewCard key={answer.id} answer={answer} />
        ))}
      </section>

      <h2 className="text-lg font-semibold text-forest">Reported questions</h2>

      {reports.isLoading && <p className="text-ink-soft">Loading reports…</p>}
      {reports.data && reports.data.length === 0 && (
        <Card>
          <p className="p-1 text-ink">Nothing in the queue 🎉 All clear.</p>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {reports.data?.map((r) => (
          <ReportCard key={r.id} report={r} fixKey={fixKey} resolve={resolve} />
        ))}
      </div>
    </div>
  );
}

function AnswerReviewCard({ answer }: { answer: AnswerReviewDTO }) {
  const qc = useQueryClient();
  const [scanUrl, setScanUrl] = useState<string | null>(null);
  const [bin, setBin] = useState<BinColor | null>(null);
  const selectedBin = answer.teacherBin ?? bin;
  const applying = answer.resolutionState === 'waiting' || answer.resolutionState === 'active';
  const resolve = useMutation({
    mutationFn: () =>
      apiPost<ResolveAnswerReviewResponse>(`/teacher/answer-reviews/${answer.id}/resolve`, {
        bin: selectedBin,
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['teacher', 'answer-reviews'] }),
  });
  const scan = useMutation({
    mutationFn: () => apiGet<{ url: string }>(`/teacher/answer-reviews/${answer.id}/scan`),
    onSuccess: ({ url }) => setScanUrl(url),
  });
  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="font-semibold text-forest">
            {answer.studentName} · Q{answer.questionNumber}
          </h3>
          <p className="text-sm text-ink-soft">
            {answer.paperTitle} · Attempt {answer.attemptIndex}
          </p>
        </div>
        <p className="text-sm text-ink-soft">
          {answer.reviewReason === 'low_scan_confidence'
            ? 'Low scan readability'
            : 'AI requested teacher review'}
          {answer.scanConfidence !== null
            ? ` · Reading confidence: ${Math.round(answer.scanConfidence * 100)}%`
            : ''}
          . This answer has no grade or mastery bucket.
        </p>
        <MathText>{answer.questionText}</MathText>
        {answer.submittedAnswer && (
          <p className="whitespace-pre-wrap text-sm">{answer.submittedAnswer}</p>
        )}
        {answer.hasScan && (
          <Button variant="ghost" disabled={scan.isPending} onClick={() => scan.mutate()}>
            {scan.isPending ? 'Loading scan…' : 'View original scan'}
          </Button>
        )}
        {scan.isError && (
          <p className="text-sm text-danger">Could not open this scan. Please try again.</p>
        )}
        {scanUrl && (
          <img
            src={scanUrl}
            alt={`Answer scan from ${answer.studentName}`}
            className="max-h-[36rem] w-full rounded-card border border-line object-contain"
          />
        )}
        <fieldset disabled={resolve.isPending || Boolean(answer.teacherBin)}>
          <legend className="mb-2 text-sm font-semibold">Assign a mastery bucket</legend>
          <div className="flex flex-wrap gap-2">
            {BIN_ORDER.map((choice) => {
              const ramp = binRamp(choice);
              return (
                <label
                  key={choice}
                  className="flex cursor-pointer items-center gap-2 rounded-card border px-3 py-2 text-sm"
                  style={{ background: ramp.bg, borderColor: ramp.border, color: ramp.text }}
                >
                  <input
                    type="radio"
                    name={`bucket-${answer.id}`}
                    value={choice}
                    checked={selectedBin === choice}
                    onChange={() => setBin(choice)}
                  />
                  {BIN_LABEL[choice]} · {marksForBin(choice, answer.maxMarks)}/{answer.maxMarks}
                </label>
              );
            })}
          </div>
        </fieldset>
        <p className="text-sm text-ink-soft">
          Saving closes this review and updates the student's marks, mastery bucket and review card.
        </p>
        {applying ? (
          <p role="status" className="text-sm text-ink-soft">
            Applying {BIN_LABEL[answer.teacherBin!]}… You can leave this page while it finishes.
          </p>
        ) : (
          <Button disabled={!selectedBin || resolve.isPending} onClick={() => resolve.mutate()}>
            {resolve.isPending
              ? 'Saving…'
              : answer.teacherBin
                ? 'Retry saving bucket'
                : 'Save bucket'}
          </Button>
        )}
        {(resolve.isError || answer.resolutionState === 'failed') && (
          <p role="alert" className="text-sm text-danger">
            {resolve.error?.message ?? 'The bucket could not be applied. Retry saving it.'}
          </p>
        )}
      </div>
    </Card>
  );
}

function ReportCard({
  report,
  fixKey,
  resolve,
}: {
  report: Report;
  fixKey: ReturnType<
    typeof useMutation<unknown, Error, { questionId: string; correctOptionIdx: number }>
  >;
  resolve: ReturnType<
    typeof useMutation<unknown, Error, { id: string; status: 'RESOLVED' | 'DISMISSED' }>
  >;
}) {
  const q = report.question;
  const [savedIdx, setSavedIdx] = useState<number | null>(null);

  return (
    <Card>
      <div className="flex flex-col gap-3 p-1">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-700">
            ⚑ {REASON_LABEL[report.reason] ?? report.reason}
          </span>
          <span className="text-ink-faint" data-numeric="">
            {report.board} · {report.grade}
          </span>
          {q && (
            <span className="text-ink-faint">
              {q.subtopic.topic.name} › {q.subtopic.name}
            </span>
          )}
        </div>

        {report.note && <p className="text-sm italic text-ink-soft">“{report.note}”</p>}

        {q ? (
          <>
            <div className="text-ink">
              <MathText>{q.prompt}</MathText>
            </div>

            {q.type === 'MCQ' && q.options && (
              <div className="flex flex-col gap-1.5">
                {q.options.map((opt, i) => {
                  const isCurrent = (savedIdx ?? q.correctOptionIdx) === i;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={fixKey.isPending}
                        onClick={() =>
                          fixKey.mutate(
                            { questionId: q.id, correctOptionIdx: i },
                            { onSuccess: () => setSavedIdx(i) },
                          )
                        }
                        className="rounded border px-2 py-1 text-left text-sm"
                        style={{
                          borderColor: isCurrent ? 'var(--brand-forest)' : 'var(--surface-sunk)',
                          background: isCurrent ? 'var(--green-bg)' : '#fff',
                        }}
                      >
                        <span className="font-semibold" data-numeric="">
                          {String.fromCharCode(65 + i)}.
                        </span>{' '}
                        <MathText>{opt}</MathText>
                      </button>
                      {isCurrent && (
                        <span className="text-xs font-semibold text-forest">✓ correct key</span>
                      )}
                    </div>
                  );
                })}
                <p className="text-fs-11 text-ink-faint">
                  Click an option to set it as the correct answer key.
                </p>
              </div>
            )}

            {q.type !== 'MCQ' && q.correctAnswer && (
              <div className="rounded-card border border-line p-3 text-sm text-ink-soft">
                <span className="font-semibold">Model answer: </span>
                <MathText>{q.correctAnswer}</MathText>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-ink-faint">Question no longer exists.</p>
        )}

        <div className="flex gap-3">
          <Button
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ id: report.id, status: 'RESOLVED' })}
          >
            Resolve
          </Button>
          <Button
            variant="ghost"
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ id: report.id, status: 'DISMISSED' })}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </Card>
  );
}
